import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { expertDashboardApi } from '../../../lib/http';
import { ExpertService } from '../../../types';

interface ServiceFormPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editService: ExpertService | null;
  currency?: string;
}

interface FormData {
  name: string;
  description: string;
  duration_min: number;
  price: string;
  deposit: string;
}

interface FormErrors {
  name?: string;
  duration_min?: string;
  price?: string;
  deposit?: string;
}

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1h 30min' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' },
];

const getCurrencySymbol = (code: string): string => {
  const symbols: Record<string, string> = {
    USD: '$', EUR: '€', UAH: '₴', GBP: '£',
    AUD: 'A$', CAD: 'C$', CHF: 'Fr', PLN: 'zł',
  };
  return symbols[code] || code;
};

const ServiceFormPanel: React.FC<ServiceFormPanelProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editService,
  currency,
}) => {
  const isEditMode = !!editService;
  const nameInputRef = useRef<HTMLInputElement>(null);

  const initialFormData: FormData = {
    name: '',
    description: '',
    duration_min: 60,
    price: '',
    deposit: '',
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [customDuration, setCustomDuration] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (editService) {
      setFormData({
        name: editService.name,
        description: editService.description || '',
        duration_min: editService.duration_min,
        price: (editService.price_cents / 100).toFixed(2),
        deposit: editService.deposit_cents > 0 ? (editService.deposit_cents / 100).toFixed(2) : '',
      });
      // Check if duration is custom
      const isCustom = !DURATION_OPTIONS.some(opt => opt.value === editService.duration_min);
      setCustomDuration(isCustom);
    } else {
      setFormData(initialFormData);
      setCustomDuration(false);
    }
    setErrors({});
  }, [editService, isOpen]);

  // Focus on name input when panel opens
  useEffect(() => {
    if (isOpen && nameInputRef.current) {
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      return expertDashboardApi.services.create(data);
    },
    onSuccess: () => {
      toast.success('Service created successfully');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create service');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return expertDashboardApi.services.update(id, data);
    },
    onSuccess: () => {
      toast.success('Service updated successfully');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update service');
    },
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Service name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (formData.duration_min < 5) {
      newErrors.duration_min = 'Duration must be at least 5 minutes';
    } else if (formData.duration_min > 480) {
      newErrors.duration_min = 'Duration cannot exceed 8 hours';
    }

    const priceValue = parseFloat(formData.price);
    if (!formData.price || isNaN(priceValue)) {
      newErrors.price = 'Price is required';
    } else if (priceValue < 0) {
      newErrors.price = 'Price cannot be negative';
    } else if (priceValue > 10000) {
      newErrors.price = 'Price cannot exceed $10,000';
    }

    if (formData.deposit) {
      const depositValue = parseFloat(formData.deposit);
      if (isNaN(depositValue)) {
        newErrors.deposit = 'Invalid deposit amount';
      } else if (depositValue < 0) {
        newErrors.deposit = 'Deposit cannot be negative';
      } else if (depositValue > priceValue) {
        newErrors.deposit = 'Deposit cannot exceed price';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    const submitData = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      duration_min: formData.duration_min,
      padding_before_min: 0,
      padding_after_min: 0,
      price_cents: Math.round(parseFloat(formData.price) * 100),
      deposit_cents: formData.deposit ? Math.round(parseFloat(formData.deposit) * 100) : 0,
      active: true, // Always active
    };

    if (isEditMode && editService) {
      updateMutation.mutate({ id: editService.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    setCustomDuration(false);
    onClose();
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity"
        onClick={handleClose}
      />

      {/* Centered Modal - Full screen on mobile, centered on desktop */}
      <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center md:p-4">
        <div className="bg-white md:rounded-2xl shadow-2xl w-full max-w-lg h-[100dvh] md:h-auto md:max-h-[90vh] flex flex-col overflow-hidden rounded-t-2xl md:rounded-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditMode ? 'Edit Service' : 'New Service'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close panel"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-5">
            {/* Service Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                Service Name <span className="text-red-500">*</span>
              </label>
              <input
                ref={nameInputRef}
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow ${
                  errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="e.g., Haircut, Consultation, Massage"
              />
              {errors.name && <p className="mt-1.5 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-gray-400 text-xs font-normal">(Optional)</span>
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                maxLength={500}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                placeholder="Describe what's included in this service..."
              />
              <p className="mt-1 text-xs text-gray-500 text-right">{formData.description.length}/500</p>
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Duration <span className="text-red-500">*</span>
              </label>
              {!customDuration ? (
                <div className="flex flex-wrap gap-2">
                  {DURATION_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, duration_min: option.value })}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border transition-all ${
                        formData.duration_min === option.value
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setCustomDuration(true)}
                    className="px-3 py-2 text-sm font-medium rounded-lg border border-dashed border-gray-300 text-gray-500 hover:border-purple-300 hover:text-purple-600"
                  >
                    Custom
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.duration_min}
                    onChange={(e) => setFormData({ ...formData, duration_min: parseInt(e.target.value) || 0 })}
                    min={5}
                    max={480}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-600">minutes</span>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomDuration(false);
                      setFormData({ ...formData, duration_min: 60 });
                    }}
                    className="ml-2 text-sm text-purple-600 hover:text-purple-700"
                  >
                    Use preset
                  </button>
                </div>
              )}
              {errors.duration_min && <p className="mt-1.5 text-sm text-red-600">{errors.duration_min}</p>}
            </div>

            {/* Pricing Section */}
            <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Pricing
              </h3>

              <div className="grid grid-cols-2 gap-4">
                {/* Price */}
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Price <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{getCurrencySymbol(currency || 'USD')}</span>
                    <input
                      type="number"
                      id="price"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className={`block w-full pl-8 pr-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        errors.price ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.price && <p className="mt-1.5 text-sm text-red-600">{errors.price}</p>}
                </div>

                {/* Deposit */}
                <div>
                  <label htmlFor="deposit" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Deposit <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{getCurrencySymbol(currency || 'USD')}</span>
                    <input
                      type="number"
                      id="deposit"
                      step="0.01"
                      min="0"
                      value={formData.deposit}
                      onChange={(e) => setFormData({ ...formData, deposit: e.target.value })}
                      className={`block w-full pl-8 pr-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        errors.deposit ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.deposit && <p className="mt-1.5 text-sm text-red-600">{errors.deposit}</p>}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer - Sticky at bottom with safe area padding for mobile */}
        <div className="px-6 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:pb-4 border-t border-gray-200 bg-gray-50 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:bg-purple-300 transition-colors"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {isEditMode ? 'Saving...' : 'Creating...'}
              </span>
            ) : (
              isEditMode ? 'Save Changes' : 'Create Service'
            )}
          </button>
        </div>
        </div>
      </div>
    </>
  );
};

export default ServiceFormPanel;
